// -----------------------------------------------
// FUTURE TEMPLATE ENGINE ROADMAP (resumo):
// 1. Filtros: | date:'DD/MM/YYYY' | number:'pt-BR':2 | upper | currency:'BRL'
// 2. Condicionais: {{#if cond}} ... {{else if ...}} {{else}} {{/if}}
// 3. Loops: {{#each itens}} ... {{/each}} com índice, total, agregações.
// 4. Nós custom: ConditionalNode, LoopNode, PageBreakNode, HeaderNode, FooterNode, ImageVariableNode.
// 5. Tabelas dinâmicas: definição de colunas + agregações + formatação condicional.
// 6. Expressões: {{ (qtd * preco) | currency:'BRL' }} (interpretador seguro).
import React, { useCallback, useId, useRef, useEffect, forwardRef, createContext, useContext } from "react";
import InputField from "../InputField/InputField";
import type { InputFieldProps } from "../InputField/InputField";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { LinkNode } from "@lexical/link";
import { ListNode, ListItemNode } from "@lexical/list";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { TableNode, TableCellNode, TableRowNode, INSERT_TABLE_COMMAND } from "@lexical/table";
import {
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { CLEAR_EDITOR_COMMAND } from "lexical";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $getRoot, $getSelection } from "lexical";
import clsx from "clsx";
import type { EditorState, LexicalEditor } from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { VariableNode } from "./VariableNode";
import { InsertVariablePlugin } from "./InsertVariablePlugin";
import Button from "../Button/Button";
import { SmartFont } from "../SmartFonts/SmartFonts";
import SelectField from "../SelectField/SelectField";
import Chips from "../Chips/Chips";

const FONT_FAMILIES = [
  { value: "Inter", label: "Inter" },
  { value: "Roboto", label: "Roboto" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Space Grotesk", label: "Space Grotesk" },
  { value: "Arial", label: "Arial" },
  { value: "Georgia", label: "Georgia" },
];

const FONT_SIZES = [
  { value: "12px", label: "12px" },
  { value: "14px", label: "14px" },
  { value: "16px", label: "16px" },
  { value: "18px", label: "18px" },
  { value: "24px", label: "24px" },
  { value: "32px", label: "32px" },
];

const DEFAULT_FONT = "Inter";
const DEFAULT_SIZE = "16px";

type Variable = { label: string; value: string };
// ---- Novos tipos base para futura engine de templates ----
type TemplateVariableMeta = {
  name: string;
  type?: string;
  required?: boolean;
  example?: string;
  description?: string;
};

type TemplateValidationIssue = {
  kind: "missing-variable" | "unknown-variable" | "unknown-filter" | "syntax-error";
  detail: string;
  location?: { index: number };
};

type TemplateValidationResult = {
  usedVariables: string[];
  missingVariables: string[];
  issues: TemplateValidationIssue[];
};

type InputWysiwygProps = Omit<InputFieldProps<string>, "as" | "type" | "onChange" | "value"> & {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  theme?: "light" | "dark";
  id?: string;
  disabled?: boolean;
  customValidation?: (value: string) => string | undefined;
  autoFocus?: boolean;
  maxLength?: number;
  variables?: Variable[];
  exportPdf?: boolean;
  enableVariables?: boolean;
  fontFamilies?: { value: string; label: string }[]; // <-- Adicionado
  fontSizes?: { value: string; label: string }[];    // <-- Adicionado
  smartFontProps?: Partial<React.ComponentProps<typeof SmartFont>>; // <-- Adicionado
  // Novas props para futura expansão
  variableCatalog?: TemplateVariableMeta[];           // catálogo com meta (tipo, obrigatório etc.)
  enableSidebar?: boolean;                            // mostra painel lateral de variáveis usadas
  datasetPreview?: Record<string, unknown>;           // dataset exemplo para preview rápido (futuro)
  onValidateTemplate?: (r: TemplateValidationResult) => void; // callback validação
  // flags para futuras features
  experimentalFilters?: boolean;
  experimentalLoops?: boolean;
};

const VariablesContext = createContext<Variable[] | undefined>(undefined);

function useVariables(propsVariables?: Variable[]) {
  const contextVars = useContext(VariablesContext);
  return propsVariables ?? contextVars ?? [];
}

function ResetOnValueChange({ value }: { value: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Leia o HTML atual do editor
    let currentHtml = "";
    editor.getEditorState().read(() => {
      currentHtml = $generateHtmlFromNodes(editor, null);
    });

    // Só atualize se o valor externo for diferente do valor atual
    if (value !== currentHtml) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(value || "<p></p>", "text/html");
        const lexicalRoot = $getRoot();
        lexicalRoot.clear();
        const nodes = $generateNodesFromDOM(editor, dom);
        nodes.forEach(node => {
          lexicalRoot.append(node);
        });
      });
    }
  }, [value, editor]);

  return null;
}

function WysiwygToolbar({ disabled }: { disabled?: boolean }) {
  const [editor] = useLexicalComposerContext();

  // Fluent UI + Tailwind: mais alto, mais largo, sombra, foco, hover, arredondado, transição suave
  const btnBase = clsx(
    "relative", // para ripple do Button
    "rounded-lg", // mais arredondado
    "border border-transparent",
    "bg-white dark:bg-gray-900",
    "hover:bg-primary-50 dark:hover:bg-primary-900",
    "focus:outline-none focus:ring-2 focus:ring-primary-400",
    "transition-all duration-150",
    "shadow-sm hover:shadow-md",
    "text-gray-700 dark:text-gray-200",
    "disabled:opacity-50 disabled:pointer-events-none"
  );
  const iconProps = { width: 22, height: 22 };

  return (
    <div className="wysiwyg-toolbar mb-2 flex gap-2 flex-wrap">
      {/* Bold */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold"); }}
        tooltip="Negrito (Ctrl+B)"
        ariaLabel="Negrito"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="4" y="15" fontWeight="bold" fontSize="16" fill="currentColor">B</text>
          </svg>
        }
      />
      {/* Italic */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic"); }}
        tooltip="Itálico (Ctrl+I)"
        ariaLabel="Itálico"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="4" y="15" fontStyle="italic" fontSize="16" fill="currentColor">I</text>
          </svg>
        }
      />
      {/* Underline */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline"); }}
        tooltip="Sublinhado (Ctrl+U)"
        ariaLabel="Sublinhado"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="4" y="15" fontSize="16" fill="currentColor" style={{ textDecoration: "underline" }}>U</text>
          </svg>
        }
      />
      {/* Strikethrough */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough"); }}
        tooltip="Tachado"
        ariaLabel="Tachado"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="4" y="15" fontSize="16" fill="currentColor" style={{ textDecoration: "line-through" }}>S</text>
          </svg>
        }
      />
      {/* Heading H1 */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => {
          editor.update(() => {
            $setBlocksType($getSelection(), () => new HeadingNode('h1'));
          });
        }}
        tooltip="Título 1"
        ariaLabel="Título 1"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="2" y="15" fontWeight="bold" fontSize="16" fill="currentColor">H1</text>
          </svg>
        }
      />
      {/* Heading H2 */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => {
          editor.update(() => {
            $setBlocksType($getSelection(), () => new HeadingNode('h2'));
          });
        }}
        tooltip="Título 2"
        ariaLabel="Título 2"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="2" y="15" fontWeight="bold" fontSize="16" fill="currentColor">H2</text>
          </svg>
        }
      />
      {/* Heading H3 */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => {
          editor.update(() => {
            $setBlocksType($getSelection(), () => new HeadingNode('h3'));
          });
        }}
        tooltip="Título 3"
        ariaLabel="Título 3"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="2" y="15" fontWeight="bold" fontSize="16" fill="currentColor">H3</text>
          </svg>
        }
      />
      {/* Link */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        onClick={() => { editor.dispatchCommand(TOGGLE_LINK_COMMAND, null); }}
        tooltip="Inserir/Remover Link"
        ariaLabel="Link"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <path d="M7 13a5 5 0 0 1 0-7" stroke="currentColor" strokeWidth="2"/>
            <path d="M13 7a5 5 0 0 1 0 7" stroke="currentColor" strokeWidth="2"/>
            <line x1="8" y1="12" x2="12" y2="8" stroke="currentColor" strokeWidth="2"/>
          </svg>
        }
      />
      {/* Blockquote */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => {
          editor.update(() => {
            $setBlocksType($getSelection(), () => new QuoteNode());
          });
        }}
        tooltip="Citação"
        ariaLabel="Citação"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="4" y="15" fontSize="16" fill="currentColor" style={{ fontStyle: "italic" }}>&quot;</text>
          </svg>
        }
      />
      {/* Código */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code"); }}
        tooltip="Código"
        ariaLabel="Código"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <rect x="3" y="5" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
            <text x="7" y="15" fontSize="12" fill="currentColor">{`</>`}</text>
          </svg>
        }
      />
      {/* Lista não ordenada */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined); }}
        tooltip="Lista não ordenada"
        ariaLabel="Lista não ordenada"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <circle cx="6" cy="10" r="2" fill="currentColor"/>
            <rect x="10" y="9" width="7" height="2" rx="1" fill="currentColor"/>
          </svg>
        }
      />
      {/* Lista ordenada */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined); }}
        tooltip="Lista ordenada"
        ariaLabel="Lista ordenada"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <text x="2" y="14" fontSize="12" fill="currentColor">1.</text>
            <rect x="10" y="9" width="7" height="2" rx="1" fill="currentColor"/>
          </svg>
        }
      />
      {/* Remover lista */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined); }}
        tooltip="Remover lista"
        ariaLabel="Remover lista"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <line x1="4" y1="16" x2="16" y2="4" stroke="currentColor" strokeWidth="2"/>
            <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="2"/>
          </svg>
        }
      />
      {/* Tabela */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(INSERT_TABLE_COMMAND, { rows: "2", columns: "2" }); }}
        tooltip="Inserir tabela"
        ariaLabel="Inserir tabela"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <rect x="3" y="5" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1"/>
            <line x1="8" y1="5" x2="8" y2="15" stroke="currentColor" strokeWidth="1"/>
            <line x1="12" y1="5" x2="12" y2="15" stroke="currentColor" strokeWidth="1"/>
          </svg>
        }
      />
      {/* Limpar formatação */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        onClick={() => { editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined); }}
        tooltip="Limpar formatação"
        ariaLabel="Limpar formatação"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <path d="M4 4L16 16" stroke="currentColor" strokeWidth="2"/>
            <rect x="6" y="6" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="2"/>
          </svg>
        }
      />
      {/* Desfazer */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(UNDO_COMMAND, undefined); }}
        tooltip="Desfazer"
        ariaLabel="Desfazer"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <path d="M7 7L3 10L7 13" stroke="currentColor" strokeWidth="2"/>
            <path d="M3 10H13C15.7614 10 18 12.2386 18 15V15" stroke="currentColor" strokeWidth="2"/>
          </svg>
        }
      />
      {/* Refazer */}
      <Button
        type="button"
        size="tiny"
        variant="text"
        buttonClassName={btnBase}
        disabled={disabled}
        onClick={() => { editor.dispatchCommand(REDO_COMMAND, undefined); }}
        tooltip="Refazer"
        ariaLabel="Refazer"
        icon={
          <svg {...iconProps} viewBox="0 0 20 20" fill="none">
            <path d="M13 7L17 10L13 13" stroke="currentColor" strokeWidth="2"/>
            <path d="M17 10H7C4.23858 10 2 12.2386 2 15V15" stroke="currentColor" strokeWidth="2"/>
          </svg>
        }
      />
      {/* Variáveis (se desejar, pode adicionar um botão para abrir menu de variáveis) */}
      {/* Emoji (exemplo de botão, funcionalidade depende de plugin extra) */}
      {/* <Button ... /> */}
    </div>
  );
}

function CharCountPlugin({ max }: { max?: number }) {
  const [editor] = useLexicalComposerContext();
  const [count, setCount] = React.useState(0);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        setCount($getRoot().getTextContent().length);
      });
    });
  }, [editor]);

  return (
    <div className="text-xs text-right text-gray-500 mt-1">
      {count}{max ? " / " + max : ""} caracteres
    </div>
  );
}

// Temas Lexical prontos usando TailwindCSS
const lexicalThemes = {
  light: {
    paragraph: "text-gray-900",
    heading: {
      h1: "text-2xl font-bold text-gray-900",
      h2: "text-xl font-semibold text-gray-900",
      h3: "text-lg font-semibold text-gray-900",
    },
    list: {
      ul: "list-disc ml-6 text-gray-900",
      ol: "list-decimal ml-6 text-gray-900",
      listitem: "",
    },
    text: {
      bold: "font-bold text-primary-600",
      italic: "italic",
      underline: "underline",
    },
    link: "text-blue-600 underline",
    table: "border border-gray-300",
    tableCell: "border border-gray-300 px-2 py-1",
    variable: "bg-yellow-100 text-yellow-800 px-1 rounded",
  },
  dark: {
    paragraph: "text-gray-100",
    heading: {
      h1: "text-2xl font-bold text-gray-100",
      h2: "text-xl font-semibold text-gray-100",
      h3: "text-lg font-semibold text-gray-100",
    },
    list: {
      ul: "list-disc ml-6 text-gray-100",
      ol: "list-decimal ml-6 text-gray-100",
      listitem: "",
    },
    text: {
      bold: "font-bold text-primary-400",
      italic: "italic",
      underline: "underline",
    },
    link: "text-blue-400 underline",
    table: "border border-gray-700",
    tableCell: "border border-gray-700 px-2 py-1",
    variable: "bg-yellow-900 text-yellow-100 px-1 rounded",
  },
};

const InputWysiwyg = forwardRef<HTMLDivElement, InputWysiwygProps>(({
  value,
  onChange,
  label,
  error,
  errorMessage,
  helperText,
  required,
  placeholder,
  className,
  theme = "light",
  id,
  disabled = false,
  customValidation,
  autoFocus = false,
  maxLength,
  variables,
  exportPdf = false,
  enableVariables = false,
  fontFamilies = FONT_FAMILIES, // <-- Use a prop aqui
  fontSizes = FONT_SIZES,       // <-- Use a prop aqui
  smartFontProps = {},
  variableCatalog,
  enableSidebar = false,
  datasetPreview,
  onValidateTemplate,
  ...rest
}, ref) => {
  // Estado para fonte e tamanho
  const [fontFamily, setFontFamily] = React.useState(fontFamilies[0]?.value ?? DEFAULT_FONT);
  const [fontSize, setFontSize] = React.useState(fontSizes[2]?.value ?? DEFAULT_SIZE);

  const generatedId = useId();
  const inputId = id || generatedId;

  const contentEditableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof ref === "function") {
      ref(contentEditableRef.current);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = contentEditableRef.current;
    }
  }, [ref]);

  const editorConfig = {
    namespace: "InputWysiwyg",
    theme: lexicalThemes[theme],
    onError(error: Error) { throw error; },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      LinkNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      VariableNode,
    ],
  };

  const handleInputFieldChange = useCallback(
    (val: unknown) => {
      if (typeof val === "string") {
        onChange(val);
      }
    },
    [onChange]
  );

  const handleChange = useCallback((editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const htmlString: string = $generateHtmlFromNodes(editor, null);
      onChange(htmlString);
    });
  }, [onChange]);

  const customError = customValidation ? customValidation(value) : undefined;

  useEffect(() => {
    if (autoFocus && contentEditableRef.current) {
      requestAnimationFrame(() => {
        contentEditableRef.current?.focus();
      });
    }
  }, [autoFocus]);

  const availableVariables = useVariables(variables);

  // Estado derivado de variáveis usadas
  const [usedVariables, setUsedVariables] = React.useState<string[]>([]);
  const [missingVariables, setMissingVariables] = React.useState<string[]>([]);
  const [validationIssues, setValidationIssues] = React.useState<TemplateValidationIssue[]>([]);

  // Revalida sempre que value ou catálogo mudarem
  useEffect(() => {
    const result = validateTemplate(value || "", variableCatalog);
    setUsedVariables(result.usedVariables);
    setMissingVariables(result.missingVariables);
    setValidationIssues(result.issues);
    onValidateTemplate?.(result);
  }, [value, variableCatalog, onValidateTemplate]);

  // Preview simples substituindo apenas variáveis exatas (sem filtros) — placeholder
  const computeQuickPreview = useCallback(() => {
    if (!datasetPreview) return value;
    return (value || "").replace(VARIABLE_REGEX, (_full, spanVar, mustacheVar) => {
      const key = (spanVar || mustacheVar || "").trim();
      const val = datasetPreview[key];
      return (val === null || val === undefined) ? "" : String(val);
    });
  }, [value, datasetPreview]);

  const quickPreviewHtml = computeQuickPreview();

  // Helper: scroll/flash para variável clicada
  const scrollToVariable = useCallback((name: string) => {
    if (!contentEditableRef.current) return;
    const el = contentEditableRef.current.querySelector(`[data-variable="${name}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add("ring", "ring-yellow-400");
      setTimeout(() => el.classList.remove("ring", "ring-yellow-400"), 1200);
    }
  }, []);

  // Cria chips a partir das variáveis usadas
  const variableChips = React.useMemo(
    () =>
      usedVariables.map(v => ({
        id: v,
        label: v,
        tooltip: variableCatalog?.find(c => c.name === v)?.description,
      })),
    [usedVariables, variableCatalog]
  );

  // Remove todas as ocorrências de uma variável e sincroniza via onChange
  const removeVariableOccurrences = useCallback(
    (name: string) => {
      if (!contentEditableRef.current) return;
      const spans = contentEditableRef.current.querySelectorAll<HTMLSpanElement>(
        `span[data-variable="${name}"]`
      );
      if (spans.length === 0) return;
      spans.forEach(s => s.remove());
      // Após remover no DOM, gera novo HTML para disparar ciclo externo -> ResetOnValueChange atualiza Lexical
      const newHtml = contentEditableRef.current.innerHTML;
      onChange(newHtml);
    },
    [onChange]
  );

  return (
    <InputField
      as="custom"
      value={value}
      onChange={handleInputFieldChange}
      label={label}
      error={error || !!customError}
      errorMessage={customError || errorMessage}
      helperText={helperText}
      required={required}
      placeholder={placeholder}
      className={clsx("wysiwyg-input-field", className)}
      id={inputId}
      maxLength={maxLength}
      theme={theme}
      {...(rest as Omit<InputFieldProps<string>, "as" | "type" | "onChange" | "value" | "theme">)}
    >
      {/* Select de fonte e tamanho */}
      <div className="flex gap-4 mb-2">
        <SelectField
          label="Fonte"
          options={fontFamilies}
          value={fontFamily}
          onChange={value => {
            if (Array.isArray(value)) {
              setFontFamily(value[0] ?? "");
            } else {
              setFontFamily(value);
            }
          }}
          theme={theme}
          disabled={disabled}
          className="min-w-[160px]"
        />
        <SelectField
          label="Tamanho"
          options={fontSizes}
          value={fontSize}
          onChange={value => {
            if (Array.isArray(value)) {
              setFontSize(value[0] ?? "");
            } else {
              setFontSize(value);
            }
          }}
          theme={theme}
          disabled={disabled}
          className="min-w-[120px]"
        />
      </div>
      {/* SmartFont controla toda a tipografia do editor */}
      <SmartFont
        family={fontFamily}
        weights={[400, 600, 700]}
        fallback={["Arial", "sans-serif"]}
        fontPriority={["local", "google"]}
        scale="body"
        theme={theme}
        style={{ fontSize: fontSize }}
        {...smartFontProps}
      >
        <LexicalComposer initialConfig={editorConfig}>
          <ResetOnValueChange value={value} />
          {enableVariables && (
            <InsertVariablePlugin variables={availableVariables.map(v => v.value)} />
          )}
          <WysiwygToolbar disabled={disabled} />
          <div
            className={clsx(
              "wysiwyg-editor",
              "min-h-[120px] w-full px-0 py-0",
              "bg-transparent border-none shadow-none",
              "text-inherit font-inherit"
            )}
            role="textbox"
            aria-labelledby={label ? `${inputId}-label` : undefined}
            aria-invalid={!!error}
            aria-required={required}
            aria-describedby={
              error
                ? "error-message"
                : helperText
                ? "helper-text"
                : undefined
            }
          >
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  ref={contentEditableRef}
                  className={clsx(
                    "wysiwyg-content-editable",
                    "outline-none min-h-[80px] bg-transparent",
                    "text-inherit font-inherit",
                    "placeholder-gray-400 dark:placeholder-gray-500",
                    disabled && "opacity-60 pointer-events-none"
                  )}
                  aria-label={label}
                  spellCheck={true}
                  contentEditable={!disabled}
                  tabIndex={disabled ? -1 : 0}
                  style={{ fontFamily, fontSize }}
                />
              }
              placeholder={
                <div className="wysiwyg-placeholder select-none pointer-events-none text-gray-400 dark:text-gray-500">
                  {placeholder}
                </div>
              }
              ErrorBoundary={({ children }) => <>{children}</>}
            />
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
            <TablePlugin />
            <OnChangePlugin onChange={handleChange} />
          </div>
          <CharCountPlugin max={maxLength} />
          {exportPdf && <ExportPdfButton contentEditableRef={contentEditableRef} />}
        </LexicalComposer>
      </SmartFont>
      {/* Painel lateral/aba inferior para variáveis e validação */}
      {enableSidebar && (
        <div className="mt-4 border-t pt-3 text-xs space-y-4">
          {/* Chips de variáveis usadas */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <strong>Variáveis (chips)</strong>
              <span className="text-[10px] text-gray-400">{variableChips.length}</span>
            </div>
            <Chips
              items={variableChips}
              onDelete={name => removeVariableOccurrences(name)}
              theme={theme}
              renderChip={chip => (
                <button
                  type="button"
                  onClick={() => scrollToVariable(chip.id)}
                  className={clsx(
                    "flex items-center gap-1 px-2 h-7 rounded-md border text-[11px] font-medium",
                    variableCatalog?.some(c => c.name === chip.id)
                      ? "bg-green-50 dark:bg-green-900/30 border-green-300 text-green-700 dark:text-green-300"
                      : "bg-amber-50 dark:bg-amber-900/30 border-amber-300 text-amber-700 dark:text-amber-300"
                  )}
                  title="Clique para localizar; use X para remover ocorrências"
                >
                  {chip.label}
                </button>
              )}
            />
          </div>
          <div className="flex flex-wrap gap-6">
            {/* Lista simples já existente (mantida para referência) */}
            <div>
              <strong className="block mb-1">Variáveis usadas ({usedVariables.length})</strong>
              {usedVariables.length === 0 && <span className="text-gray-400">Nenhuma</span>}
              <ul className="flex flex-wrap gap-1">
                {usedVariables.map(v => (
                  <li
                    key={v}
                    onClick={() => scrollToVariable(v)}
                    className={clsx(
                      "px-1.5 py-0.5 rounded cursor-pointer border",
                      variableCatalog?.some(c => c.name === v)
                        ? "bg-green-50 dark:bg-green-900/30 border-green-300 text-green-700 dark:text-green-300"
                        : "bg-amber-50 dark:bg-amber-900/30 border-amber-300 text-amber-700 dark:text-amber-300"
                    )}
                    title="Clique para localizar"
                  >
                    {v}
                  </li>
                ))}
              </ul>
            </div>
            {variableCatalog && (
              <div>
                <strong className="block mb-1">Obrigatórias faltando ({missingVariables.length})</strong>
                {missingVariables.length === 0 && <span className="text-gray-400">Nenhuma</span>}
                <ul className="flex flex-wrap gap-1">
                  {missingVariables.map(v => (
                    <li
                      key={v}
                      className="px-1.5 py-0.5 rounded border bg-red-50 dark:bg-red-900/30 border-red-300 text-red-700 dark:text-red-300"
                      title="Variável obrigatória não usada"
                    >
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {validationIssues.length > 0 && (
              <div className="min-w-[220px]">
                <strong className="block mb-1">Issues ({validationIssues.length})</strong>
                <ul className="space-y-0.5 max-h-40 overflow-auto pr-1">
                  {validationIssues.map((i, idx) => (
                    <li key={idx} className={clsx(
                      i.kind === "missing-variable" && "text-red-600 dark:text-red-400",
                      i.kind === "unknown-variable" && "text-amber-600 dark:text-amber-400",
                      i.kind === "unknown-filter" && "text-purple-600",
                      i.kind === "syntax-error" && "text-orange-600"
                    )}>
                      [{i.kind}] {i.detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {datasetPreview && (
              <div className="min-w-[220px]">
                <strong className="block mb-1">Preview rápido</strong>
                <div
                  className="border p-2 rounded max-h-40 overflow-auto text-[11px] leading-snug bg-gray-50 dark:bg-gray-800"
                  dangerouslySetInnerHTML={{ __html: quickPreviewHtml || "<em>Vazio</em>" }}
                />
                <div className="mt-1 text-[10px] text-gray-400">
                  Substituição simples (sem filtros/loops ainda).
                </div>
              </div>
            )}
          </div>
          <div className="text-[10px] text-gray-400">
            Clique em uma variável / chip para localizar. Remova via X (remove todas as ocorrências).
          </div>
        </div>
      )}
    </InputField>
  );
});

InputWysiwyg.displayName = "InputWysiwyg";

/**
 * Componente WYSIWYG (What You See Is What You Get) baseado em Lexical para edição de texto rico.
 * Permite formatação, listas, links, tabelas, variáveis dinâmicas e exportação para PDF.
 * Suporta customização visual total via prop `theme` (tema Lexical).
 *
 * @component
 * @param {object} props - Propriedades do componente.
 * @param {string} props.value - Valor HTML atual do editor.
 * @param {(value: string) => void} props.onChange - Função chamada ao alterar o conteúdo.
 * @param {string} [props.label] - Rótulo do campo.
 * @param {boolean} [props.error] - Indica se há erro de validação.
 * @param {string} [props.errorMessage] - Mensagem de erro a ser exibida.
 * @param {string} [props.helperText] - Texto de ajuda exibido abaixo do campo.
 * @param {boolean} [props.required] - Indica se o campo é obrigatório.
 * @param {string} [props.placeholder] - Placeholder exibido quando vazio.
 * @param {string} [props.className] - Classes CSS adicionais para o campo.
 * @param {object} [props.theme] - Objeto de tema Lexical para customização visual total. Veja https://lexical.dev/docs/themes
 * @param {string} [props.id] - ID do campo.
 * @param {boolean} [props.disabled=false] - Desabilita a edição.
 * @param {(value: string) => string|undefined} [props.customValidation] - Função de validação customizada.
 * @param {boolean} [props.autoFocus=false] - Foca automaticamente ao montar.
 * @param {number} [props.maxLength] - Limite máximo de caracteres.
 * @param {Array<{label: string, value: string}>} [props.variables] - Lista de variáveis disponíveis para inserção.
 * @param {React.Ref<HTMLDivElement>} [ref] - Ref para o elemento ContentEditable.
 * @returns {JSX.Element} O componente InputWysiwyg.
 *
 * @example
 * // Uso básico
 * <InputWysiwyg
 *   value={valor}
 *   onChange={setValor}
 *   label="Descrição"
 *   placeholder="Digite aqui..."
 * />
 *
 * @example
 * // Customizando o tema Lexical
 * <InputWysiwyg
 *   value={valor}
 *   onChange={setValor}
 *   theme={{
 *     paragraph: "text-blue-700 font-serif",
 *     heading: { h1: "text-3xl font-bold", h2: "text-2xl" },
 *     list: "list-disc ml-6",
 *     text: { bold: "font-bold text-primary-600" }
 *   }}
 * />
 *
 * @example
 * // Com variáveis dinâmicas
 * <InputWysiwyg
 *   value={valor}
 *   onChange={setValor}
 *   variables={[
 *     { label: "Nome do Cliente", value: "cliente_nome" },
 *     { label: "Data do Evento", value: "evento_data" }
 *   ]}
 * />
 *
 * @example
 * // Exportando para PDF (botão já incluso no componente)
 * <InputWysiwyg
 *   value={valor}
 *   onChange={setValor}
 * />
 */
function ExportPdfButton({ contentEditableRef }: { contentEditableRef: React.RefObject<HTMLDivElement | null> }) {
  const [editor] = useLexicalComposerContext();

  return (
    <Button
      type="button"
      size="small"
      onClick={async () => {
        if (contentEditableRef.current && editor) {
          await exportToPdf(editor);
        }
      }}
      className="ml-2"
      title="Exportar PDF"
    >
      Exportar PDF
    </Button>
  );
}

// ---- Regex centralizado para variáveis e (futuro) pipeline de filtros ----
const VARIABLE_REGEX =
  /<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>|{{\s*([\w\d_]+)(?:\s*\|[^}]*)?\s*}}/g;

// Utilitário simples: extrai variáveis cruas (ignora filtros por enquanto)
function extractVariables(html: string): string[] {
  const set = new Set<string>();
  let m;
  while ((m = VARIABLE_REGEX.exec(html))) {
    const raw = m[1] || m[2];
    if (raw) set.add(raw.trim());
  }
  return Array.from(set);
}

// Validação básica inicial (stub) — evoluir com parsing de filtros/condicionais
function validateTemplate(html: string, declared?: TemplateVariableMeta[]): TemplateValidationResult {
  // ...existing code (extract used)...
  const used = extractVariables(html);
  const declaredNames = (declared || []).map(v => v.name);
  const missing = declared
    ? declared.filter(v => v.required && !used.includes(v.name)).map(v => v.name)
    : [];
  const unknown = declared
    ? used.filter(u => !declaredNames.includes(u))
    : []; // se não há catálogo, não marcamos como unknown
  const issues: TemplateValidationIssue[] = [
    ...missing.map(name => ({
      kind: "missing-variable" as const,
      detail: `Variável obrigatória não usada: ${name}`,
    })),
    ...unknown.map(name => ({
      kind: "unknown-variable" as const,
      detail: `Variável não declarada no catálogo: ${name}`,
    })),
  ];
  return { usedVariables: used, missingVariables: missing, issues };
}

async function replaceVariablesInHtml(html: string): Promise<string> {
  // TODO: evoluir para aplicar filtros e expressões dentro de {{ var | filtro:'arg' }}
  // Busca todas as variáveis no HTML
  const variableRegex = /<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>|{{\s*([\w\d_]+)\s*}}/g;
  const variables = new Set<string>();
  let match;
  while ((match = variableRegex.exec(html))) {
    variables.add(match[1] || match[2]);
  }

  // Busca valores na API
  const apiUrl = process.env.REACT_APP_VARIABLES_API_URL;
  if (!apiUrl) {
    throw new Error("REACT_APP_VARIABLES_API_URL is not defined");
  }
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ variables: Array.from(variables) }),
  });

  const values = await response.json();

  // Substitui no HTML
  return html.replace(variableRegex, (_, var1, var2) => {
    const key = var1 || var2;
    return values[key] ?? "";
  });
}

export default InputWysiwyg;
export { VariablesContext };
export type { InputWysiwygProps };

async function exportToPdf(editor: LexicalEditor) {
  const html = $generateHtmlFromNodes(editor, null);
  const htmlWithValues = await replaceVariablesInHtml(html);

  // Dynamically import html2pdf.js only on the client
  const html2pdf = (await import("html2pdf.js")).default;

  // Crie um elemento temporário para renderizar o HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlWithValues;
  document.body.appendChild(tempDiv);

  await html2pdf(tempDiv).save();

  document.body.removeChild(tempDiv);
}